import React, { useState, useContext } from 'react';
import { Button, VStack, Text, Box, useToast } from '@chakra-ui/react';
import { InfluencerLocation } from '@foodfluence/shared';
import { useInfluencerLocations } from 'contexts/InfluencerLocationContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { influencerApi } from 'services/api/influencer';
import LocationForm from 'features/profile/components/LocationForm';
import { getInfluencerPrimaryLocation } from 'utils/locationUtils';
import ModalShell from './ui/ModalShell';
import { ff } from '../theme';

interface InfluencerLocationPickerProps {
  onConfirm: (location: InfluencerLocation) => void;
}

const InfluencerLocationPicker: React.FC<InfluencerLocationPickerProps> = ({ onConfirm }) => {
  const toast = useToast();
  const { influencerLocations } = useInfluencerLocations();
  const { influencerData } = useContext(InfluencerDataContext);
  const [pendingNewLocation, setPendingNewLocation] = useState<InfluencerLocation | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleCancel = () => {
    const primary = getInfluencerPrimaryLocation(influencerLocations);
    if (primary) onConfirm(primary);
  };

  const handleFormSave = (newLocation: InfluencerLocation) => {
    const existing = influencerLocations.find((loc) => {
      if (newLocation.zipCode && loc.zipCode) return loc.zipCode === newLocation.zipCode;
      return loc.city === newLocation.city && loc.state === newLocation.state;
    });
    if (existing) {
      onConfirm(existing);
      return;
    }
    setPendingNewLocation(newLocation);
  };

  const handleMakeDefault = async () => {
    if (!influencerData?.id || !pendingNewLocation) return;
    setIsSaving(true);
    try {
      const shiftedExisting = influencerLocations.map((loc, index) => ({
        city: loc.city,
        state: loc.state,
        country: loc.country,
        zipCode: loc.zipCode,
        lat: loc.lat,
        lng: loc.lng,
        maxCollabDistance: loc.maxCollabDistance,
        locationPriority: index + 2,
        ...(loc.docId && { docId: loc.docId }),
      }));
      await influencerApi.updateLocations({
        influencerId: influencerData.id,
        locations: [
          {
            city: pendingNewLocation.city,
            state: pendingNewLocation.state,
            country: pendingNewLocation.country,
            zipCode: pendingNewLocation.zipCode,
            lat: pendingNewLocation.lat,
            lng: pendingNewLocation.lng,
            maxCollabDistance: pendingNewLocation.maxCollabDistance,
            locationPriority: 1,
          },
          ...shiftedExisting,
        ],
      });
      onConfirm(pendingNewLocation);
    } catch {
      toast({ title: "Couldn't save location", status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUseTemporarily = () => {
    if (pendingNewLocation) onConfirm(pendingNewLocation);
  };

  const confirmLabel =
    pendingNewLocation?.zipCode ||
    (pendingNewLocation ? `${pendingNewLocation.city}, ${pendingNewLocation.state}` : '');

  return (
    <>
      <Box
        borderWidth="1px"
        borderColor={ff.line}
        borderRadius="20px"
        p={4}
        bg="white"
      >
        <LocationForm
          onCancel={handleCancel}
          onSave={(loc) => handleFormSave(loc as InfluencerLocation)}
          isEditing={false}
        />
      </Box>

      {/* Confirm modal — only shown when user enters a location not already in their profile */}
      <ModalShell
        isOpen={!!pendingNewLocation}
        onClose={() => setPendingNewLocation(null)}
        title={`Make ${confirmLabel} your primary location?`}
        footer={
          <VStack spacing={3} w="full" mt={5}>
            <Button w="full" variant="ink" isLoading={isSaving} onClick={handleMakeDefault}>
              Yes, make it my default
            </Button>
            <Button w="full" variant="inkOutline" isDisabled={isSaving} onClick={handleUseTemporarily}>
              No, I am just traveling
            </Button>
          </VStack>
        }
      >
        <Text fontSize="15px" color={ff.text2} lineHeight="1.45">
          This will be used to source opportunities in your area.
        </Text>
      </ModalShell>
    </>
  );
};

export default InfluencerLocationPicker;
